import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Drawer,
  Input,
  Modal,
  Progress,
  Row,
  Select,
  Skeleton,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  message as antMessage,
  theme,
} from 'antd';
import {
  UserOutlined,
  LinkOutlined,
  CloudServerOutlined,
  HeartOutlined,
  SyncOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
  HistoryOutlined,
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  MinusCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { authFetch } from '../../utils/api';
import { tokenManager } from '../../utils/tokenManager';
import { BrandScopeNotice } from '../../components/BrandScopeNotice';
import { calculateNodeHealthRate, toFailurePercent } from './healthRate';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const AUTO_REFRESH_INTERVAL_MS = 30_000;
const SCHEDULE_INTERVAL_MS = 5 * 60 * 1000;

type SupportedProtocol =
  | 'sk6'
  | 'sk6-ws'
  | 'anytls'
  | 'trojan'
  | 'hysteria2'
  | 'vless-reality'
  | 'vless-fb-cdn';

const PROTOCOL_LABEL: Record<SupportedProtocol, string> = {
  sk6: 'SK6 直連',
  'sk6-ws': 'SK6-WS CDN',
  anytls: 'AnyTLS',
  trojan: 'Trojan',
  hysteria2: 'Hysteria2 (UDP)',
  'vless-reality': 'VLESS Reality',
  'vless-fb-cdn': 'VLESS CF Workers',
};

interface TotalsData {
  totalUsers: number;
  totalNodes: number;
}

interface ConnectionsData {
  totalConnections: number;
  nodes?: NodeConnectionInfo[];
}

interface BlockingInfo {
  nodeId: number;
  total?: number;
  failures?: number;
  cdnFallbacks?: number;
  failureRate: number;
}

interface NodeConnectionInfo {
  nodeId: number;
  nodeName: string;
  nodeRegion: string;
  connections: number;
}

interface SummaryCell {
  nodeId: number;
  protocol: string;
  enabled: boolean;
  success: boolean | null;
  latencyMs: number | null;
  errorMessage: string | null;
  testedAt: string | null;
  triggeredBy: 'schedule' | 'manual' | null;
  // 這筆數值是不是真握手驗證的結果（true=TCP/TLS/HTTP 真握手；
  // false=send-only probe 已送達但沒做真握手，例如 Hysteria2 UDP 無回應）。
  // 舊版後端不回此欄位 → undefined，前端 fallback 用 latencyMs 推導。
  verified?: boolean | null;
  // Hysteria2 productionProof 真握手證據鮮度（fresh/stale/missing）；非 hy2 為 null。
  proofFreshness?: 'fresh' | 'stale' | 'missing' | null;
}

interface SummaryNode {
  nodeId: number;
  name: string;
  displayName: string | null;
  ip: string;
  region: string;
  nodeType: string;
  isEnabled: boolean;
  healthStatus: string;
  enabledProtocols: SupportedProtocol[];
  cells: SummaryCell[];
}

/**
 * 判斷某協議是否「適用於」該節點類型。
 *
 * - vless 節點：只走 CF Workers 的 `vless-fb-cdn`，sk6 family 一律不適用
 * - sk6 (與其他非 vless) 節點：vless-fb-cdn 不適用，其餘皆適用
 *
 * 「不適用」≠「未啟用」：
 * - 不適用 → cell 顯示「—」+ 灰色 + Tooltip「此協議不適用該節點類型」
 * - 未啟用 → cell 顯示「未啟用」+ 灰色 + Tooltip「節點 vpn_config 未啟用」
 * - 失敗 → cell 顯示「失敗」+ 紅色
 */
function isProtocolApplicable(
  nodeType: string,
  protocol: SupportedProtocol,
): boolean {
  if (nodeType === 'vless') {
    return protocol === 'vless-fb-cdn';
  }
  return protocol !== 'vless-fb-cdn';
}

interface SummaryPayload {
  protocols: SupportedProtocol[];
  nodes: SummaryNode[];
  generatedAt: string;
  lastScheduledRunAt: string | null;
}

interface HistoryRecord {
  id: number;
  nodeId: number;
  protocol: string;
  success: boolean;
  latencyMs: number | null;
  errorMessage: string | null;
  testedAt: string;
  triggeredBy: 'schedule' | 'manual';
  triggeredByAdminId: number | null;
}

/**
 * 一格是不是「已送達但未深驗」——即 probe 只送了封包、沒做真握手驗證。
 *
 * 目前只有 Hysteria2 (UDP) 會落在這：QUIC + obfs 對非握手封包本來就不回應，
 * 收不到回應是探測方法的天生限制，不是節點壞。後端新版把這種情況記成
 * success=true 但 latencyMs=null（不再塞 5000ms 假延遲）。
 *
 * 判定優先序：
 * 1. 後端有明確回 `verified` 欄位 → 直接用（success 且 verified===false）。
 * 2. 舊版後端沒回 verified（undefined）→ fallback：success 但 latencyMs==null
 *    當成 send-only（相容既有已落地資料，不會把它誤 render 成 5000ms/失敗）。
 */
function isUnverifiedSend(cell: SummaryCell): boolean {
  if (cell.success !== true) return false;
  if (cell.verified === true) return false;
  if (cell.verified === false) return true;
  // verified 未知（舊版後端沒回此欄位，或修法部署後第一輪 cron 尚未覆蓋的
  // 既有資料）→ 保守推導：
  //   - latencyMs==null → send-only（真握手成功一定帶延遲）
  //   - hysteria2 且 latencyMs===5000 → 舊版 probeUdp 的 timeout 佔位假延遲
  //     （就是本次要根除的假象；避免部署後第一輪 cron 前還 render 成「5000ms 慢」）
  if (cell.latencyMs == null) return true;
  return cell.protocol === 'hysteria2' && cell.latencyMs === 5000;
}

function classifyCell(cell: SummaryCell): {
  color: string;
  label: string;
  icon: React.ReactNode;
} {
  if (!cell.enabled) {
    return {
      color: 'default',
      label: '未啟用',
      icon: <MinusCircleOutlined style={{ color: '#bfbfbf' }} />,
    };
  }
  if (cell.success === null) {
    return {
      color: 'warning',
      label: '尚未測試',
      icon: <MinusCircleOutlined style={{ color: '#faad14' }} />,
    };
  }
  if (cell.success === false) {
    return {
      color: 'error',
      label: '失敗',
      icon: <CloseCircleTwoTone twoToneColor="#ff4d4f" />,
    };
  }
  // 歷史 proof 不能把本次 send-only 探測提升成真握手成功。
  if (isUnverifiedSend(cell)) {
    return {
      color: 'processing',
      label: '已送達·未深驗',
      icon: <MinusCircleOutlined style={{ color: '#1677ff' }} />,
    };
  }
  if ((cell.latencyMs ?? 0) >= 200) {
    return {
      color: 'warning',
      label: '慢',
      icon: <CheckCircleTwoTone twoToneColor="#faad14" />,
    };
  }
  return {
    color: 'success',
    label: 'OK',
    icon: <CheckCircleTwoTone twoToneColor="#52c41a" />,
  };
}

function fromNow(time: string | null): string {
  if (!time) return '-';
  const diffMs = Date.now() - new Date(time).getTime();
  if (Number.isNaN(diffMs)) return time;
  const sec = Math.max(0, Math.round(diffMs / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 48) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  return `${day}d ago`;
}

/**
 * 營運總覽 Dashboard 頁面
 *
 * - 4 個統計卡片：總用戶數、活躍連線數、節點數、節點健康率
 * - 節點 × 協議監控矩陣（5 分鐘排程 + 手動觸發 + 歷史紀錄）
 * - 每 30 秒自動刷新（同時刷新 totals + protocol summary）
 *
 * （原 /protocol-health 頁面已併入本頁，避免重複內容）
 */
export const DashboardPage: React.FC = () => {
  const { token } = theme.useToken();

  const [totalUsers, setTotalUsers] = useState(0);
  const [totalConnections, setTotalConnections] = useState(0);
  const [totalNodes, setTotalNodes] = useState(0);
  const [healthRate, setHealthRate] = useState(0);
  const [perNodeConnections, setPerNodeConnections] = useState<
    Record<number, number>
  >({});
  const [perNodeFailureRate, setPerNodeFailureRate] = useState<
    Record<number, number>
  >({});

  const [summary, setSummary] = useState<SummaryPayload | null>(null);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [triggerOpen, setTriggerOpen] = useState(false);
  const [triggerNodeIds, setTriggerNodeIds] = useState<number[]>([]);
  const [triggerProtocols, setTriggerProtocols] = useState<SupportedProtocol[]>(
    [],
  );
  const [triggerLoading, setTriggerLoading] = useState(false);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyData, setHistoryData] = useState<HistoryRecord[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(50);
  const [historyNodeId, setHistoryNodeId] = useState<number | undefined>();
  const [historyProtocol, setHistoryProtocol] = useState<string | undefined>();
  const [historySuccess, setHistorySuccess] = useState<string | undefined>();
  const [historyDateRange, setHistoryDateRange] =
    useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);

  // ── Data loaders ──────────────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    setRefreshing(true);
    try {
      const [totalsRes, connectionsRes, blockingRes, summaryRes] =
        await Promise.all([
          authFetch('/api/admin/stats/totals'),
          authFetch('/api/admin/stats/connections'),
          authFetch('/api/admin/stats/blocking-status'),
          authFetch('/api/admin/protocol-health/summary'),
        ]);

      const [totalsResult, connectionsResult, blockingResult, summaryResult] =
        await Promise.all([
          totalsRes.json() as Promise<{ code: number; data: TotalsData }>,
          connectionsRes.json() as Promise<{
            code: number;
            data: ConnectionsData;
          }>,
          blockingRes.json() as Promise<{ code: number; data: BlockingInfo[] }>,
          summaryRes.json() as Promise<{
            code: number;
            data: SummaryPayload;
          }>,
        ]);

      if (totalsResult.code === 0) {
        setTotalUsers(totalsResult.data.totalUsers ?? 0);
        setTotalNodes(totalsResult.data.totalNodes ?? 0);
      }

      if (connectionsResult.code === 0) {
        setTotalConnections(connectionsResult.data.totalConnections ?? 0);
        const nodes: NodeConnectionInfo[] = connectionsResult.data.nodes ?? [];
        const conns: Record<number, number> = {};
        for (const n of nodes) {
          conns[n.nodeId] = n.connections;
        }
        setPerNodeConnections(conns);
      }

      const failureRates: Record<number, number> = {};
      if (blockingResult.code === 0) {
        const list: BlockingInfo[] = Array.isArray(blockingResult.data)
          ? blockingResult.data
          : [];
        for (const b of list) {
          failureRates[b.nodeId] = toFailurePercent(b.failureRate);
        }
        setPerNodeFailureRate(failureRates);
      }

      if (summaryResult.code === 0 && summaryResult.data) {
        setSummary(summaryResult.data);
        setHealthRate(
          calculateNodeHealthRate(summaryResult.data.nodes ?? [], failureRates),
        );
      }
    } catch {
      // ignore network errors on auto-refresh
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // initial + manual refresh tick
  useEffect(() => {
    fetchData();
  }, [fetchData, refreshTick]);

  // auto refresh
  //   - tab 不可見（document.hidden）時跳過該輪 tick，不做背景輪詢
  //   - 未登入（token 已被清）時跳過，避免無認證請求（authFetch 亦會攔 401
  //     並導回登入，此處為前置防呆，少打一輪必然失敗的請求）
  //   - 分頁重新可見時立即刷新一次（補上暫停期間錯過的資料）
  useEffect(() => {
    if (!autoRefresh) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    timerRef.current = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      if (!tokenManager.hasToken()) return;
      setRefreshTick((t) => t + 1);
    }, AUTO_REFRESH_INTERVAL_MS);

    const onVisible = () => {
      if (!document.hidden && tokenManager.hasToken()) {
        setRefreshTick((t) => t + 1);
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [autoRefresh]);

  // history drawer
  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(historyPage));
      params.set('pageSize', String(historyPageSize));
      if (historyNodeId) params.set('nodeId', String(historyNodeId));
      if (historyProtocol) params.set('protocol', historyProtocol);
      if (historySuccess) params.set('success', historySuccess);
      if (historyDateRange) {
        params.set('startTime', historyDateRange[0].toISOString());
        params.set('endTime', historyDateRange[1].toISOString());
      }
      const res = await authFetch(
        `/api/admin/protocol-health/results?${params.toString()}`,
      );
      const result = await res.json();
      if (result.code === 0 && result.data) {
        setHistoryData(
          Array.isArray(result.data.list)
            ? (result.data.list as HistoryRecord[])
            : [],
        );
        setHistoryTotal(Number(result.data.total) || 0);
      }
    } catch (err) {
      antMessage.error(`歷史查詢失敗: ${(err as Error).message}`);
    } finally {
      setHistoryLoading(false);
    }
  }, [
    historyPage,
    historyPageSize,
    historyNodeId,
    historyProtocol,
    historySuccess,
    historyDateRange,
  ]);

  useEffect(() => {
    if (historyOpen) fetchHistory();
  }, [historyOpen, fetchHistory]);

  // ── Derived ──────────────────────────────────────────────────────────

  const protocols = summary?.protocols ?? [];

  // 排序：non-vless 節點排前，vless 節點排底；同類型內維持後端回傳順序（穩定排序）。
  // 用 .map+sort 避免 mutating 後端陣列。
  const sortedNodes = useMemo(() => {
    const nodes = summary?.nodes ?? [];
    const indexed = nodes.map((n, i) => ({ n, i }));
    indexed.sort((a, b) => {
      const aIsVless = a.n.nodeType === 'vless' ? 1 : 0;
      const bIsVless = b.n.nodeType === 'vless' ? 1 : 0;
      if (aIsVless !== bIsVless) return aIsVless - bIsVless;
      return a.i - b.i;
    });
    return indexed.map((x) => x.n);
  }, [summary]);

  const overallStats = useMemo(() => {
    if (!summary) return { total: 0, ok: 0, fail: 0, untested: 0 };
    let total = 0;
    let ok = 0;
    let fail = 0;
    let untested = 0;
    for (const node of summary.nodes) {
      for (const cell of node.cells) {
        if (!cell.enabled) continue;
        total++;
        if (cell.success === null) untested++;
        else if (cell.success) ok++;
        else fail++;
      }
    }
    return { total, ok, fail, untested };
  }, [summary]);

  const nextScheduledEta = (() => {
    if (!summary?.lastScheduledRunAt) return null;
    const next =
      new Date(summary.lastScheduledRunAt).getTime() + SCHEDULE_INTERVAL_MS;
    const diff = next - Date.now();
    if (diff <= 0) return '即將開始';
    const min = Math.round(diff / 60_000);
    if (min < 1) return '<1 分鐘';
    return `約 ${min} 分鐘後`;
  })();

  const healthColor =
    healthRate >= 80
      ? token.colorSuccess
      : healthRate >= 50
        ? token.colorWarning
        : token.colorError;

  const summaryCards = [
    {
      key: 'users',
      title: '總用戶數',
      value: totalUsers,
      icon: <UserOutlined style={{ fontSize: 28, color: token.colorPrimary }} />,
      testId: 'dashboard-users',
    },
    {
      key: 'connections',
      title: '活躍連線數',
      value: totalConnections,
      icon: <LinkOutlined style={{ fontSize: 28, color: token.colorInfo }} />,
      testId: 'dashboard-connections',
    },
    {
      key: 'nodes',
      title: '節點數',
      value: totalNodes,
      icon: (
        <CloudServerOutlined
          style={{ fontSize: 28, color: token.colorWarning }}
        />
      ),
      testId: 'dashboard-nodes',
    },
    {
      key: 'health',
      title: '節點健康率',
      value: healthRate,
      suffix: '%',
      icon: <HeartOutlined style={{ fontSize: 28, color: healthColor }} />,
      valueStyle: { color: healthColor },
      testId: 'dashboard-health',
    },
  ];

  // ── Handlers ─────────────────────────────────────────────────────────

  const handleManualTrigger = async () => {
    setTriggerLoading(true);
    try {
      const body: Record<string, unknown> = {};
      if (triggerNodeIds.length > 0) body.nodeIds = triggerNodeIds;
      if (triggerProtocols.length > 0) body.protocols = triggerProtocols;
      const res = await authFetch('/api/admin/protocol-health/check', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      const result = await res.json();
      if (result.code === 0) {
        antMessage.success(
          `健康檢查完成: ${result.data?.ok ?? 0} ok / ${
            result.data?.fail ?? 0
          } fail / ${result.data?.total ?? 0} 總計`,
        );
        setTriggerOpen(false);
        setRefreshTick((t) => t + 1);
      } else {
        antMessage.error(result.msg || '觸發失敗');
      }
    } catch (err) {
      antMessage.error(`觸發失敗: ${(err as Error).message}`);
    } finally {
      setTriggerLoading(false);
    }
  };

  const renderCell = (cell: SummaryCell, nodeType: string) => {
    const applicable = isProtocolApplicable(
      nodeType,
      cell.protocol as SupportedProtocol,
    );

    // 不適用 → 顯示「—」+ 灰色 + 明確 Tooltip，避免誤判為 bug
    if (!applicable) {
      return (
        <Tooltip title="此協議不適用該節點類型">
          <Tag
            data-testid="protocol-health-cell"
            data-status="不適用"
            color="default"
            style={{
              margin: 0,
              minWidth: 72,
              display: 'inline-flex',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              cursor: 'help',
              opacity: 0.4,
            }}
          >
            <Space size={4}>
              <MinusCircleOutlined style={{ color: '#bfbfbf' }} />
              <span>—</span>
            </Space>
          </Tag>
        </Tooltip>
      );
    }

    const cls = classifyCell(cell);
    const unverified = isUnverifiedSend(cell);
    const proofNote =
      cell.protocol === 'hysteria2' && cell.proofFreshness
        ? cell.proofFreshness === 'fresh'
          ? '歷史 proof: 30 天內有紀錄（不代表本次握手或下發狀態）'
          : cell.proofFreshness === 'stale'
            ? '歷史 proof: 已超過 30 天（不影響新版下發）'
            : '歷史 proof: 無紀錄（不影響新版下發）'
        : null;
    const tip = cell.enabled ? (
      <div>
        <div>狀態: {cls.label}</div>
        {unverified ? (
          <div>
            探測方式: 只送出封包、未做真握手（Hysteria2 走 QUIC+obfs，對非握手
            封包本來就不回應，收不到回應是探測方法的限制，不代表節點故障）
          </div>
        ) : (
          <div>
            延遲: {cell.latencyMs == null ? '-' : `${cell.latencyMs} ms`}
          </div>
        )}
        {proofNote && <div>{proofNote}</div>}
        <div>
          測試於:{' '}
          {cell.testedAt
            ? dayjs(cell.testedAt).format('YYYY-MM-DD HH:mm:ss')
            : '-'}
        </div>
        <div>觸發: {cell.triggeredBy ?? '-'}</div>
        {cell.errorMessage && <div>錯誤: {cell.errorMessage}</div>}
      </div>
    ) : (
      <span>此協議適用該節點，但目前未在 vpn_config 啟用（請至節點管理編輯 config）</span>
    );
    return (
      <Tooltip title={tip}>
        <Tag
          data-testid="protocol-health-cell"
          data-status={cls.label}
          color={cls.color}
          style={{
            margin: 0,
            minWidth: 72,
            display: 'inline-flex',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            cursor: 'pointer',
          }}
        >
          <Space size={4}>
            {cls.icon}
            <span>
              {cell.enabled
                ? cell.success === null
                  ? '—'
                  : // send-only 未深驗（含過渡期舊 5000 佔位列）→ 顯示中性標記，
                    // 不 render `${latencyMs}ms`。這樣舊列 latencyMs=5000 也不會
                    // 露出「5000ms」的嚇人假延遲。
                    unverified
                    ? cls.label
                    : cell.latencyMs == null
                      ? cls.label
                      : `${cell.latencyMs}ms`
                : '未啟用'}
            </span>
          </Space>
        </Tag>
      </Tooltip>
    );
  };

  // ── UI ──────────────────────────────────────────────────────────────

  return (
    <div className="page-enter" data-testid="dashboard-page">
      {/* Header */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          marginBottom: 24,
        }}
      >
        <Title level={3} style={{ margin: 0 }} data-testid="dashboard-title">
          營運總覽
        </Title>
        <Space wrap>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <SyncOutlined spin={refreshing} style={{ marginRight: 4 }} />
            自動刷新 30s
          </Text>
          <Checkbox
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          >
            啟用自動刷新
          </Checkbox>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => setRefreshTick((t) => t + 1)}
            loading={refreshing}
          >
            重新整理
          </Button>
          <Button
            icon={<HistoryOutlined />}
            onClick={() => setHistoryOpen(true)}
          >
            歷史紀錄
          </Button>
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            onClick={() => setTriggerOpen(true)}
            data-testid="protocol-health-trigger-btn"
          >
            手動觸發健康檢查
          </Button>
        </Space>
      </div>

      {/* 4 統計卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {summaryCards.map((card) => (
          <Col xs={24} sm={12} lg={6} key={card.key}>
            <Card hoverable data-testid={card.testId}>
              {loading ? (
                <Skeleton active paragraph={{ rows: 1 }} />
              ) : (
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: 16 }}
                >
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 12,
                      background: token.colorBgTextHover,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {card.icon}
                  </div>
                  <Statistic
                    title={card.title}
                    value={card.value}
                    suffix={card.suffix}
                    valueStyle={{
                      fontSize: 28,
                      fontWeight: 600,
                      ...card.valueStyle,
                    }}
                  />
                </div>
              )}
            </Card>
          </Col>
        ))}
      </Row>

      {/* 節點 × 協議監控（原 protocol-health 頁面整合） */}
      <Title level={4} style={{ marginTop: 24, marginBottom: 12 }}>
        節點與協議監控
      </Title>

      <BrandScopeNotice kind="shared-infra" style={{ marginBottom: 12 }} />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="後台會自動每 5 分鐘檢查所有節點的所有啟用協議。手動觸發會覆寫對應節點/協議的最新結果。"
        description={
          summary
            ? `最後排程執行：${
                summary.lastScheduledRunAt
                  ? dayjs(summary.lastScheduledRunAt).format(
                      'YYYY-MM-DD HH:mm:ss',
                    )
                  : '尚未執行'
              }；下次預計：${nextScheduledEta ?? '-'}`
            : null
        }
      />

      <Card style={{ marginBottom: 12 }} size="small">
        <Space size={24} wrap>
          <span>
            <Badge status="success" text={`OK ${overallStats.ok}`} />
          </span>
          <span>
            <Badge status="error" text={`FAIL ${overallStats.fail}`} />
          </span>
          <span>
            <Badge status="warning" text={`未測 ${overallStats.untested}`} />
          </span>
          <span>
            <Text type="secondary">
              覆蓋率：
              {overallStats.total > 0
                ? Math.round(
                    ((overallStats.ok + overallStats.fail) /
                      overallStats.total) *
                      100,
                  )
                : 0}
              % / {overallStats.total} 個 (節點 × 協議)
            </Text>
          </span>
          <Progress
            percent={
              overallStats.total > 0
                ? Math.round((overallStats.ok / overallStats.total) * 100)
                : 0
            }
            size="small"
            status={overallStats.fail > 0 ? 'exception' : 'success'}
            style={{ width: 220 }}
          />
        </Space>
      </Card>

      <Table<SummaryNode>
        dataSource={sortedNodes}
        rowKey="nodeId"
        loading={loading}
        size="small"
        tableLayout="fixed"
        pagination={false}
        data-testid="protocol-health-matrix"
      >
        <Table.Column<SummaryNode>
          title="節點"
          dataIndex="name"
          key="name"
          width={96}
          ellipsis
          render={(_, record) => (
            <span style={{ fontWeight: 600 }} title={record.name}>
              {record.name}
            </span>
          )}
        />
        <Table.Column<SummaryNode>
          title="顯示名稱"
          dataIndex="displayName"
          key="displayName"
          width={96}
          ellipsis
          render={(_, record) => {
            const value = record.displayName?.trim() || record.name;
            return (
              <span data-testid="node-display-name-cell" title={value}>
                {value}
              </span>
            );
          }}
        />
        <Table.Column<SummaryNode>
          title="區域"
          dataIndex="region"
          key="region"
          width={60}
          ellipsis
          render={(v: string) => <span title={v}>{v || '-'}</span>}
        />
        <Table.Column<SummaryNode>
          title="IP"
          dataIndex="ip"
          key="ip"
          width={120}
          ellipsis
          render={(_, record) => (
            <span
              data-testid="node-ip-cell"
              style={{
                fontFamily:
                  'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
                fontSize: 12,
                whiteSpace: 'nowrap',
              }}
              title={record.ip}
            >
              {record.ip}
            </span>
          )}
        />
        <Table.Column<SummaryNode>
          title="類型"
          dataIndex="nodeType"
          key="nodeType"
          width={68}
          align="center"
          render={(_, record) => (
            <Tag
              color={record.nodeType === 'vless' ? 'purple' : 'blue'}
              style={{
                margin: 0,
                minWidth: 48,
                textAlign: 'center',
              }}
            >
              {record.nodeType}
            </Tag>
          )}
        />
        <Table.Column<SummaryNode>
          title="狀態"
          dataIndex="healthStatus"
          key="healthStatus"
          width={80}
          align="center"
          render={(_, record) => (
            <Tag
              color={
                record.healthStatus === 'healthy'
                  ? 'green'
                  : record.healthStatus === 'down'
                    ? 'red'
                    : 'default'
              }
              style={{
                margin: 0,
                minWidth: 56,
                textAlign: 'center',
              }}
            >
              {record.healthStatus}
            </Tag>
          )}
        />
        <Table.Column<SummaryNode>
          title="連線"
          key="connections"
          width={64}
          align="right"
          render={(_, record) => perNodeConnections[record.nodeId] ?? 0}
          sorter={(a, b) =>
            (perNodeConnections[a.nodeId] ?? 0) -
            (perNodeConnections[b.nodeId] ?? 0)
          }
        />
        <Table.Column<SummaryNode>
          title="故障率"
          key="failureRate"
          width={84}
          align="center"
          render={(_, record) => {
            const rate = perNodeFailureRate[record.nodeId] ?? 0;
            const color =
              rate < 5 ? 'success' : rate <= 20 ? 'warning' : 'error';
            return (
              <Tag
                color={color}
                style={{
                  margin: 0,
                  minWidth: 56,
                  textAlign: 'center',
                }}
              >
                {rate.toFixed(1)}%
              </Tag>
            );
          }}
          sorter={(a, b) =>
            (perNodeFailureRate[a.nodeId] ?? 0) -
            (perNodeFailureRate[b.nodeId] ?? 0)
          }
        />
        {protocols.map((protocol) => (
          <Table.Column
            key={protocol}
            title={
              <Tooltip title={protocol}>
                <span>{PROTOCOL_LABEL[protocol] ?? protocol}</span>
              </Tooltip>
            }
            dataIndex={protocol}
            width={86}
            align="center"
            render={(_, record: SummaryNode) => {
              const cell = record.cells.find((c) => c.protocol === protocol);
              if (!cell) return '-';
              return renderCell(cell, record.nodeType);
            }}
          />
        ))}
      </Table>

      <div style={{ marginTop: 12 }} data-testid="stats-table">
        <Text type="secondary">
          {summary?.generatedAt
            ? `更新於 ${dayjs(summary.generatedAt).format(
                'YYYY-MM-DD HH:mm:ss',
              )} · ${fromNow(summary.generatedAt)}`
            : '—'}
        </Text>
      </div>

      <Modal
        title="手動觸發健康檢查"
        open={triggerOpen}
        onCancel={() => setTriggerOpen(false)}
        confirmLoading={triggerLoading}
        onOk={handleManualTrigger}
        okText="開始檢查"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <Text>節點（不選 = 全部）</Text>
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              placeholder="選擇節點"
              value={triggerNodeIds}
              onChange={(vals) => setTriggerNodeIds(vals as number[])}
              options={(summary?.nodes ?? []).map((n) => ({
                value: n.nodeId,
                label: `${n.name} (${n.region})`,
              }))}
              allowClear
            />
          </div>
          <div>
            <Text>協議（不選 = 該節點所有啟用協議）</Text>
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              placeholder="選擇協議"
              value={triggerProtocols}
              onChange={(vals) =>
                setTriggerProtocols(vals as SupportedProtocol[])
              }
              options={protocols.map((p) => ({
                value: p,
                label: PROTOCOL_LABEL[p] ?? p,
              }))}
              allowClear
            />
          </div>
          <Alert
            type="warning"
            showIcon
            message="此操作會逐一探測每個 (節點, 協議) pair（每個 5s timeout），數量大時可能需要數十秒。"
          />
        </Space>
      </Modal>

      <Drawer
        title="協議健康檢查歷史"
        placement="right"
        width="80%"
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
      >
        <Space wrap style={{ marginBottom: 12 }}>
          <Input
            placeholder="節點 ID"
            allowClear
            style={{ width: 120 }}
            type="number"
            onChange={(e) => {
              setHistoryPage(1);
              const v = e.target.value.trim();
              setHistoryNodeId(v ? Number(v) : undefined);
            }}
          />
          <Select
            placeholder="協議"
            allowClear
            style={{ width: 200 }}
            value={historyProtocol}
            onChange={(value) => {
              setHistoryPage(1);
              setHistoryProtocol(value);
            }}
            options={protocols.map((p) => ({
              value: p,
              label: PROTOCOL_LABEL[p] ?? p,
            }))}
          />
          <Select
            placeholder="結果"
            allowClear
            style={{ width: 120 }}
            value={historySuccess}
            onChange={(value) => {
              setHistoryPage(1);
              setHistorySuccess(value);
            }}
            options={[
              { value: 'true', label: '成功' },
              { value: 'false', label: '失敗' },
            ]}
          />
          <RangePicker
            showTime
            onChange={(dates) => {
              setHistoryPage(1);
              setHistoryDateRange(
                dates as [dayjs.Dayjs, dayjs.Dayjs] | null,
              );
            }}
          />
          <Button
            onClick={fetchHistory}
            loading={historyLoading}
            icon={<ReloadOutlined />}
          >
            查詢
          </Button>
        </Space>

        <Table<HistoryRecord>
          dataSource={historyData}
          rowKey="id"
          loading={historyLoading}
          size="small"
          pagination={{
            current: historyPage,
            pageSize: historyPageSize,
            total: historyTotal,
            showSizeChanger: true,
            onChange: (p, ps) => {
              setHistoryPage(p);
              setHistoryPageSize(ps);
            },
          }}
        >
          <Table.Column
            dataIndex="testedAt"
            title="時間"
            width={170}
            render={(v: string) => dayjs(v).format('YYYY-MM-DD HH:mm:ss')}
          />
          <Table.Column dataIndex="nodeId" title="節點 ID" width={90} />
          <Table.Column
            dataIndex="protocol"
            title="協議"
            width={140}
            render={(v: SupportedProtocol) => PROTOCOL_LABEL[v] ?? v}
          />
          <Table.Column
            dataIndex="success"
            title="結果"
            width={80}
            render={(v: boolean) =>
              v ? (
                <Tag color="success">OK</Tag>
              ) : (
                <Tag color="error">FAIL</Tag>
              )
            }
          />
          <Table.Column
            dataIndex="latencyMs"
            title="延遲"
            width={90}
            render={(v: number | null) => (v == null ? '-' : `${v} ms`)}
          />
          <Table.Column
            dataIndex="triggeredBy"
            title="觸發"
            width={110}
            render={(v: string) => (
              <Tag color={v === 'manual' ? 'gold' : 'default'}>{v}</Tag>
            )}
          />
          <Table.Column
            dataIndex="errorMessage"
            title="錯誤訊息"
            ellipsis
            render={(v: string | null) => v || '-'}
          />
        </Table>
      </Drawer>
    </div>
  );
};
